import React, { useState } from 'react';
import axios from 'axios';
import { BiUpload, BiCheckCircle, BiErrorCircle } from 'react-icons/bi';

import { API_BASE_URL } from '../utils/urls.js';
import apiClient from '../utils/apiClient.js';
import { useOnboarding } from '../Onboarding/OnboardingContext';

// ADDED isPiCase prop here
const DocumentUpload = ({ caseId, onUploadSuccess, isPiCase }) => {
    const [file, setFile] = useState(null);
    const [status, setStatus] = useState(null); // 'uploading', 'success', 'error'
    const [message, setMessage] = useState('');
    const [formData, setFormData] = useState({
        documents_to_be_served: '',
        service_instructions: '',
        special_instructions: '',
    });

    const { runTour, stepIndex, setStepIndex } = useOnboarding();

    const token = localStorage.getItem('token');

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
        setStatus(null);
        setMessage('');
    };

    const handleFormChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!file) {
            setMessage('Please select a file to upload.');
            return;
        }

        setStatus('uploading');
        setMessage('Uploading file...');

        const uploadData = new FormData();
        uploadData.append('document', file); // 'document' must match the multer single('document') key
        
        // Append all text fields (required for formal documents)
        uploadData.append('documents_to_be_served', formData.documents_to_be_served);
        uploadData.append('service_instructions', formData.service_instructions);
        uploadData.append('special_instructions', formData.special_instructions);

        try {
            // 1. DYNAMICALLY DETERMINE THE URL BASED ON isPiCase PROP
            const uploadEndpoint = isPiCase 
                ? `${API_BASE_URL}/pi-cases/${caseId}/upload` 
                : `${API_BASE_URL}/cases/${caseId}/upload`;

            // 2. USE THE DYNAMIC ENDPOINT
            await axios.post(uploadEndpoint, uploadData, {
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
            });

            // ✅ Trigger the Checklist!
            await apiClient.post('/onboarding/events', { event_name: 'first_document_uploaded' }).catch(() => {});

            if (runTour && stepIndex === 11) {
                setStepIndex(12);
            }

            setStatus('success');
            setMessage('File uploaded successfully! Case details will refresh shortly.');
            setFile(null); // Clear file input
            onUploadSuccess(); // Trigger parent component refresh
            setTimeout(() => setStatus(null), 3000);

        } catch (err) {
            console.error('Upload Error:', err);
            setStatus('error');
            setMessage(err.response?.data?.message || 'Upload failed. Server error.');
            setTimeout(() => setStatus(null), 5000);
        }
    };

    const isUploading = status === 'uploading';

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="tour-upload-file space-y-4">
                {/* File Input */}
                <div className="border border-dashed border-gray-400 p-4 rounded-md">
                    <label htmlFor="file-upload" className="block text-sm font-medium text-gray-700 mb-2">
                        {file ? `File Selected: ${file.name}` : 'Select Document or Media'}
                    </label>
                    <input 
                        id="file-upload" 
                        type="file" 
                        onChange={handleFileChange} 
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        disabled={isUploading}
                    />
                </div>

                {/* Instruction Fields (Hidden for Field Agent simplicity, but kept for Admin/Manager use) */}
                {/* NOTE: If this component is only used by Field Agents for PROOF, these fields might be irrelevant.
                If it's used by Managers to load initial documents, they are essential. We keep them here for completeness
                as your caseRoutes.js uses them for documents.
                */}
                
                {/* Submission Status */}
                {status && (
                    <div className={`p-3 text-sm rounded-md flex items-center ${
                        status === 'success' ? 'bg-green-100 text-green-700' :
                        status === 'error' ? 'bg-red-100 text-red-700' :
                        'bg-blue-100 text-blue-700'
                    }`}>
                        {status === 'success' && <BiCheckCircle className="w-5 h-5 mr-2" />}
                        {status === 'error' && <BiErrorCircle className="w-5 h-5 mr-2" />}
                        {status === 'uploading' && <BiUpload className="w-5 h-5 mr-2 animate-pulse" />}
                        {message}
                    </div>
                )}

                <button
                    type="submit"
                    disabled={!file || isUploading}
                    className="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 transition duration-150"
                >
                    {isUploading ? 'Processing...' : 'Upload File to Case'}
                </button>
            </div>
        </form>
    );
};

export default DocumentUpload;
